import { useEffect, useState } from 'react';
import Decimal from 'decimal.js';
import { Typography, TableList, FullRow } from 'seagull';
import { utils } from "../../../../utils";
import { ServicePriceEditor } from "../../../../components/ServicePriceEditor/ServicePriceEditor";
import { serviceTemplateModel } from "../../../../models";
import { serviceCostModel, serviceModel } from "../../../../models";
import { isOneTimeInStages } from "../../../../models/service-template";
import styles from "./ServiceRowPrice.module.scss";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
function getPriceData(cost) {
  return {
    price: serviceCostModel.isRangePrice(cost) ? {
      minPrice: serviceModel.minPrice({
        cost
      }),
      maxPrice: serviceModel.price({
        cost
      })
    } : serviceModel.price({
      cost
    }),
    hasDiscount: serviceModel.discount({
      cost
    }) != null,
    discountedPrice: serviceCostModel.isRangePrice(cost) ? {
      minPrice: serviceModel.minPriceFinal({
        cost
      }),
      maxPrice: serviceModel.applyDiscount({
        cost
      })
    } : serviceModel.finalPrice({
      cost
    }, 1)
  };
}
export function ServiceRowPrice(_ref) {
  var _service$cost$discoun;
  let {
    service,
    onInlineEdit,
    onInputFocus,
    onInputBlur
  } = _ref;
  const [{
    price,
    hasDiscount,
    discountedPrice
  }, setPriceData] = useState(() => getPriceData(service.cost));
  const [anchorEl, setAnchorEl] = useState(null);
  const [priceEditorOpen, setPriceEditorOpen] = useState(false);
  const isVariable = serviceTemplateModel.isVariablePrice(service.cost);
  const unitName = serviceCostModel.formattedUnitNameShort(service.cost);
  const isInvalidDiscount = (isVariable || serviceTemplateModel.isRangePrice(service.cost)) && !!((_service$cost$discoun = service.cost.discount) !== null && _service$cost$discoun !== void 0 && _service$cost$discoun.isValue());
  useEffect(() => {
    setPriceData(getPriceData(service.cost));
  }, [service.cost]);
  return /*#__PURE__*/_jsxs(FullRow, {
    className: styles.root,
    "data-testid": "service-library-row-price",
    children: [/*#__PURE__*/_jsx(TableList.InlineControl.Button, {
      testid: "service-library-row-price-btn",
      error: false,
      tooltipProps: {
        title: discountedPrice == null ? '' : discountedPrice instanceof Decimal ? /*#__PURE__*/_jsxs(_Fragment, {
          children: [utils.formatDollarAmount(discountedPrice), price instanceof Decimal && hasDiscount && /*#__PURE__*/_jsxs(_Fragment, {
            children: [' ', /*#__PURE__*/_jsx("del", {
              children: utils.formatDollarAmount(price)
            })]
          }), isVariable && " / ".concat(unitName)]
        }) : /*#__PURE__*/_jsxs(_Fragment, {
          children: [discountedPrice.minPrice instanceof Decimal && /*#__PURE__*/_jsxs(_Fragment, {
            children: [utils.formatDollarAmount(discountedPrice.minPrice), ' - ']
          }), discountedPrice.minPrice == null && 'Up to ', discountedPrice.maxPrice instanceof Decimal ? utils.formatDollarAmount(discountedPrice.maxPrice) : '', price != null && !(price instanceof Decimal) && hasDiscount && /*#__PURE__*/_jsxs(_Fragment, {
            children: [' ', /*#__PURE__*/_jsxs("del", {
              children: [price.minPrice instanceof Decimal && /*#__PURE__*/_jsxs(_Fragment, {
                children: [utils.formatDollarAmount(price.minPrice), ' - ']
              }), price.maxPrice instanceof Decimal ? utils.formatDollarAmount(price.maxPrice) : '']
            })]
          })]
        })
      },
      onClick: e => {
        setAnchorEl(e.currentTarget);
        setPriceEditorOpen(true);
        onInputFocus === null || onInputFocus === void 0 || onInputFocus();
      },
      children: discountedPrice == null ? /*#__PURE__*/_jsx(Typography, {
        "data-testid": "service-library-row-discounted-price",
        children: "________"
      }) : discountedPrice instanceof Decimal ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(Typography, {
          as: "span",
          "data-testid": "service-library-row-discounted-price",
          children: utils.formatDollarAmount(discountedPrice)
        }), !isInvalidDiscount && typeof price === 'number' && hasDiscount && /*#__PURE__*/_jsxs(_Fragment, {
          children: [' ', /*#__PURE__*/_jsxs(Typography, {
            as: "span",
            skin: "disabled",
            textDecoration: "line-through",
            "data-testid": "service-library-row-original-price",
            children: ["$", utils.formatNumberWithCommas(price)]
          })]
        })]
      }) : /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsxs(Typography, {
          as: "span",
          "data-testid": "service-library-row-discounted-price",
          children: [!!discountedPrice.minPrice && /*#__PURE__*/_jsxs(_Fragment, {
            children: [utils.formatDollarAmount(discountedPrice.minPrice), ' - ']
          }), discountedPrice.minPrice == null && 'Up to ', discountedPrice.maxPrice instanceof Decimal ? utils.formatDollarAmount(discountedPrice.maxPrice) : '']
        }), price != null && !isInvalidDiscount && !(price instanceof Decimal) && hasDiscount && /*#__PURE__*/_jsxs(_Fragment, {
          children: [' ', /*#__PURE__*/_jsxs(Typography, {
            as: "span",
            skin: "disabled",
            textDecoration: "line-through",
            "data-testid": "service-library-row-original-price",
            children: [price.minPrice instanceof Decimal && /*#__PURE__*/_jsx(_Fragment, {
              children: /*#__PURE__*/_jsxs(Typography, {
                as: "span",
                skin: "disabled",
                textDecoration: "line-through",
                "data-testid": "service-library-row-original-price",
                children: [utils.formatDollarAmount(price.minPrice), ' - ']
              })
            }), "".concat(price.maxPrice instanceof Decimal ? utils.formatDollarAmount(price.maxPrice) : '')]
          })]
        })]
      })
    }), isVariable && /*#__PURE__*/_jsxs(Typography, {
      className: styles.unitName,
      skin: "secondary",
      ellipsis: true,
      tooltipProps: {
        title: unitName
      },
      children: ["/ ", unitName]
    }), /*#__PURE__*/_jsx(ServicePriceEditor, {
      cost: service.cost,
      editorAnchor: anchorEl,
      isOpen: priceEditorOpen,
      isBillInStages: isOneTimeInStages(service.billing),
      setIsOpen: isOpen => {
        if (!isOpen) onInputBlur === null || onInputBlur === void 0 || onInputBlur();
        setPriceEditorOpen(isOpen);
      },
      onCostChange: cost => {
        setPriceData(getPriceData(cost));
        onInlineEdit({
          cost
        });
      }
    })]
  });
}